import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react';

/** Within this distance of the bottom the reader counts as "at the newest message". */
export const STICK_TO_BOTTOM_PX = 80;

export interface StickToBottom<T extends HTMLElement> {
  ref: RefObject<T | null>;
  onScroll: () => void;
}

/**
 * Chat-list scrolling: opens at the newest message and follows new ones, late
 * content (an HTML body loading) and the list shrinking as the composer grows,
 * but leaves the view alone once the reader has scrolled up into history.
 */
export function useStickToBottom<T extends HTMLElement>(contentKey: string | number): StickToBottom<T> {
  const ref = useRef<T | null>(null);
  const pinned = useRef(true);

  const scrollToBottom = useCallback(() => {
    const el = ref.current;
    if (el && pinned.current) el.scrollTop = el.scrollHeight;
  }, []);

  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_TO_BOTTOM_PX;
  }, []);

  useLayoutEffect(() => {
    scrollToBottom();
    const el = ref.current;
    // jsdom has no ResizeObserver; the content-change path above still applies there.
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    // The container resizes when the composer grows; its children resize when a message body loads late.
    const observer = new ResizeObserver(scrollToBottom);
    observer.observe(el);
    for (const child of Array.from(el.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [contentKey, scrollToBottom]);

  return { ref, onScroll };
}
